import { faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ObjectId } from 'bson';
import classNames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast, ToastType } from 'react-toastify';

import type { IPredicate, ISegment, ISegmentUsage, Segment } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import type { ITogglerBaseProps } from '@feathr/components';
import { AlertV2, Button, EAlertV2Type, Spinner, Toggler } from '@feathr/components';
import { StoresContext } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';
import { RerunModal } from '@feathr/report_components';

import FilterChip from './FilterChip';
import LookbackMode from './LookbackMode';
import SegmentGauge from './SegmentGauge';

import styles from './EditFilters.css';
interface IProps {
  filterContext?:
    | 'campaign'
    | 'flight'
    | 'segment'
    | 'campaignGoal'
    | 'flightGoal'
    | 'facebookCampaign';
  className?: string;
  mode?: 'count' | 'crumbs' | 'reachable' | 'emails';
  segment: Segment;
  disabled?: boolean;
}

function EditFilters({
  className,
  disabled = false,
  filterContext = 'segment',
  mode = 'count',
  segment,
}: IProps): JSX.Element {
  const { Conversions } = useContext(StoresContext);
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { t } = useTranslation();
  const [usage, setUsage] = useState<ISegmentUsage>();
  const [isRerunModalOpen, toggleRerunModalOpen] = useToggle(false);

  const { observe, currentBreakpoint } = useDimensions({
    breakpoints: { SM: 0, LG: 860, XL: 1170 },
    // Anything under 1170 overlaps Segment Gauge when predicate fields present.
    updateOnBreakpointChange: true,
  });

  const getUsage = useCallback(async () => {
    const segmentUsage = await segment.getUsage();
    setUsage(segmentUsage);
  }, [segment]);

  useEffect(() => {
    getUsage();
  }, [getUsage]);

  if (!usage) {
    return <Spinner />;
  }

  const affectedGoals = usage.goals.length;
  const hasFacebook = usage.active_campaigns.some((cpn) => cpn._cls === CampaignClass.Facebook);

  const readOnly = segment.get('read_only') ?? false;
  const modeOptions: ITogglerBaseProps<ISegment['mode']>['options'] = [
    { id: 'match_all', name: 'all' },
    { id: 'match_any', name: 'any' },
  ];

  function handleAddFilter(): void {
    const predicates = segment.get('predicates') || observable([]);
    predicates.push({
      kind: 'activity',
      attr_against: 'loc_url',
      attr_type: 'string',
      comparison: 'eq',
      value: '',
      unit: 'days',
      group: [],
    });
    segment.setAttributeDirty('predicates');
  }

  const warnings: string[] = [
    t(
      'Recalculate conversions to see which campaigns, projects, and/or flights use this group as a goal.',
    ),
    t('{{count}} goal uses this group.', {
      count: affectedGoals,
    }),
  ];

  async function handleConfirmRerun(): Promise<void> {
    const params = new URLSearchParams({
      segment: segment.id,
    });
    try {
      await Conversions.rerun(params);
      segment.set({ rerun: true });
    } catch (e) {
      toast(t('Something went wrong when trying to recalculate conversions.'), {
        type: ToastType.ERROR,
      });
      throw e;
    }
  }

  return (
    <div className={className} ref={observe}>
      {!!affectedGoals && !(disabled || readOnly) && (
        <AlertV2
          description={warnings}
          title={t(
            'Editing this group can affect the conversions of campaigns and flights that use it.',
          )}
          type={EAlertV2Type.warning}
        >
          <button onClick={toggleRerunModalOpen}>{t('Recalculate conversions')}</button>
        </AlertV2>
      )}
      {isRerunModalOpen && (
        <RerunModal
          context={'Group'}
          id={segment.id}
          name={segment.name}
          onClose={toggleRerunModalOpen}
          onConfirm={handleConfirmRerun}
        />
      )}

      <div className={classNames(styles.gauge, { [styles.compact]: currentBreakpoint === 'SM' })}>
        <SegmentGauge isCompact={currentBreakpoint === 'SM'} mode={mode} segment={segment} />
      </div>
      <div className={styles.header}>
        <Trans t={t}>
          Showing people that match{' '}
          <Toggler<ISegment['mode'], ISegment>
            attribute={'mode'}
            className={styles.mode}
            disabled={disabled}
            model={segment}
            options={modeOptions}
          />{' '}
          of the following filters, looking at data{' '}
          <LookbackMode disabled={disabled} segment={segment} />.
        </Trans>
        {hasFacebook && (
          <span className={styles.facebook}>
            <FontAwesomeIcon icon={faInfoCircle} />{' '}
            {t(
              'Targets being used by active Meta campaigns currently only support the "URL" filter.',
            )}
          </span>
        )}
      </div>
      <div className={classNames(styles.content, className)}>
        {(segment.get('predicates') || observable([])).map(
          (predicate: IPredicate, index: number) => {
            function onClickRemove(): void {
              const predicates = segment.get('predicates');
              predicates.splice(index, 1);
              segment.setAttributeDirty('predicates');
            }

            return (
              <FilterChip
                disabled={disabled}
                excludeIds={campaignId ? [campaignId] : undefined}
                filterContext={
                  !filterContext.includes('Goal') && hasFacebook
                    ? 'facebookCampaign'
                    : filterContext
                }
                key={new ObjectId().toHexString()}
                model={segment}
                onClickRemove={onClickRemove}
                predicate={predicate}
              />
            );
          },
        )}

        {!disabled && (
          <Button
            name={'add_filter'}
            onClick={handleAddFilter}
            prefix={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('Add filter')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default observer(EditFilters);
